import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta, useHead } from '@unhead/react';
import './LegalPage.css';

/**
 * 개인정보 처리방침 페이지
 * 2026-10-05 사실 대조 개정(대표 지시 "틀린 내용 없는지 확인하고 수정"):
 *  - 카카오 로그인 실제 동의항목(휴대전화번호 필수·이름 선택)·상담 폼 항목·보유기간·처리위탁·파기 절차 반영
 *  - 마케팅 수신 수단에 '전화' 추가(동의 회원에게만 연락), 운영자(큐비컴)·보호책임자(대표) 명시
 */
const PrivacyPage: React.FC = () => {
  // 구조화된 데이터 (Schema.org)
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '개인정보 처리방침',
    description: '부냥 분양 정보 서비스의 개인정보 처리방침입니다. 수집하는 개인정보 항목, 처리 목적, 보유 기간 등을 안내합니다.',
    url: 'https://voonang.com/privacy',
    inLanguage: 'ko-KR',
    datePublished: '2024-01-01',
    dateModified: '2026-10-05',
    publisher: {
      '@type': 'Organization',
      name: '부냥',
      url: 'https://voonang.com',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+82-1533-8124',
        contactType: 'customer service',
        areaServed: 'KR',
        availableLanguage: 'Korean',
      },
    },
    mainEntity: {
      '@type': 'Article',
      headline: '부냥 개인정보 처리방침',
      articleSection: '개인정보 보호',
    },
  };

  useSeoMeta({
    title: '개인정보 처리방침 | 부냥',
    description: '부냥 분양 정보 서비스의 개인정보 처리방침입니다. 수집하는 개인정보 항목, 처리 목적, 보유 기간 등을 안내합니다.',
  });
  useHead({
    link: [{ rel: 'canonical', href: 'https://voonang.com/privacy' }],
    script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(schemaData) }],
  });

  // '#marketing' 등 조항 바로가기 — SPA 라 브라우저 기본 해시 이동이 안 돼 직접 스크롤 (혜택 화면 '내용 보기' 링크, 2026-10-05)
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const t = window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }), 50);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <Link to="/login" className="back-link">
            뒤로가기
          </Link>
          <h1>개인정보 처리방침</h1>
          <p className="effective-date">시행일자: 2026년 10월 5일 (이전 방침: 2024년 1월 1일 시행)</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. 개인정보의 처리 목적</h2>
            <p>
              큐비컴(서비스명 &lsquo;부냥&rsquo;, voonang.com, 사업자등록번호 502-30-39667, 이하 "회사")은 다음의 목적을 위하여 개인정보를 처리합니다.
              처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며,
              이용 목적이 변경되는 경우에는 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.
            </p>
            <ul>
              <li><strong>회원 가입 및 관리:</strong> 카카오 로그인을 통한 회원 식별, 가입 완료 안내(가입 시 카카오 알림톡 1회), 회원제 서비스 제공, 회원자격 유지·관리, 탈퇴 처리</li>
              <li><strong>서비스 제공:</strong> 분양 현장 정보 제공, 회원 전용 할인·혜택 정보 제공, 관심 현장 저장, 최근 본 현장 기록</li>
              <li><strong>상담·문의 응대:</strong> 상담 신청·사전상담·알림 신청 등 이용자가 요청한 문의에 대한 전화·문자 연락 및 처리 결과 안내</li>
              <li><strong>마케팅 및 광고 활용:</strong> 분양·할인·잔여세대 정보와 이벤트 등 광고성 정보를 카카오톡·문자·전화로 안내 (마케팅 수신에 동의한 회원에 한함, 제7조)</li>
            </ul>
          </section>

          <section>
            <h2>2. 수집하는 개인정보의 항목</h2>
            <p>회사는 카카오 간편 로그인과 상담·문의 신청 과정에서 다음과 같은 개인정보를 수집합니다.</p>
            <table className="info-table">
              <thead>
                <tr>
                  <th>구분</th>
                  <th>수집 항목</th>
                  <th>수집 목적</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>회원 필수<br />(카카오 로그인)</td>
                  <td>카카오 회원번호(고유 ID), 휴대전화번호</td>
                  <td>회원 식별·관리, 회원 전용 혜택 정보 제공, 회원 문의 응대, (마케팅 동의 시) 분양·할인 안내 연락</td>
                </tr>
                <tr>
                  <td>회원 선택<br />(카카오 로그인)</td>
                  <td>이름, 카카오톡 메시지 전송 권한, 이메일</td>
                  <td>회원 식별, 알림 발송</td>
                </tr>
                <tr>
                  <td>상담·문의 신청<br />(비회원 포함)</td>
                  <td>필수: 이름, 휴대전화번호 / 선택: 관심 현장, 문의 메모, (지인 소개 시) 소개자 이름·연락처</td>
                  <td>상담·문의 응대, 처리 결과 안내</td>
                </tr>
                <tr>
                  <td>선택</td>
                  <td>위치정보 (위·경도, 행정구역), 디바이스 정보 (User-Agent)</td>
                  <td>주변 분양 현장 안내, 지역별 방문 통계, 서비스 개선</td>
                </tr>
                <tr>
                  <td>자동</td>
                  <td>익명 세션 쿠키 (voonang_vid), 접속 IP(마지막 옥텟 마스킹), 방문 현장 기록</td>
                  <td>신규/재방문 식별, 인기 현장 통계, 서비스 품질 개선</td>
                </tr>
              </tbody>
            </table>
            <p style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280' }}>
              ※ 위치정보는 브라우저에서 사용자가 명시적으로 권한을 허용한 경우에만 수집되며,
              개인을 식별할 수 없는 형태(쿠키 ID + 위·경도)로 저장됩니다.
              방문자 통계는 익명으로 처리되며 마케팅·광고 외부 제공에 사용되지 않습니다.
            </p>
          </section>

          <section>
            <h2>3. 개인정보의 처리 및 보유 기간</h2>
            <p>
              회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에
              동의 받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.
            </p>
            <ul>
              <li><strong>회원 정보:</strong> 회원 탈퇴 시까지 (단, 관련 법령에 따라 일정 기간 보관이 필요한 경우 해당 기간)</li>
              <li><strong>상담·문의 기록:</strong> 문의일로부터 3년 (분쟁 대응 목적). 정보주체가 삭제를 요청하면 지체 없이 파기</li>
              <li><strong>마케팅 동의 정보:</strong> 동의 철회 시 또는 회원 탈퇴 시까지</li>
              <li><strong>서비스 이용 기록(방문·열람 기록):</strong> 3년</li>
            </ul>
          </section>

          <section>
            <h2>3-2. 개인정보 처리업무의 위탁</h2>
            <p>회사는 원활한 서비스 제공을 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다.</p>
            <table className="info-table">
              <thead>
                <tr>
                  <th>수탁자</th>
                  <th>위탁 업무</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>엔에이치엔클라우드㈜ (NHN Cloud)</td>
                  <td>카카오 알림톡·문자 메시지 발송</td>
                </tr>
                <tr>
                  <td>네이버㈜</td>
                  <td>상담·문의 접수 알림 이메일 발송 및 보관(네이버 메일)</td>
                </tr>
              </tbody>
            </table>
            <p>위탁 계약 시 개인정보가 안전하게 관리되도록 필요한 사항을 규정하고, 수탁자가 바뀌면 이 방침을 통해 알려 드립니다.</p>
          </section>

          <section>
            <h2>3-3. 개인정보의 파기 절차 및 방법</h2>
            <p>
              회사는 보유기간이 지나거나 처리 목적이 달성된 개인정보를 지체 없이 파기합니다.
              전자적 파일 형태의 정보는 복구할 수 없는 방법으로 삭제하며, 출력물은 분쇄 또는 소각합니다.
              법령에 따라 보존해야 하는 경우에는 다른 개인정보와 분리하여 해당 기간 동안 보관합니다.
            </p>
          </section>

          <section>
            <h2>4. 개인정보의 제3자 제공</h2>
            <p>
              회사는 정보주체의 개인정보를 제1조(개인정보의 처리 목적)에서 명시한 범위 내에서만 처리하며,
              정보주체의 동의, 법률의 특별한 규정 등 개인정보 보호법 제17조 및 제18조에 해당하는 경우에만
              개인정보를 제3자에게 제공합니다.
            </p>
            <p>회사는 상담 문의 시 입력한 개인정보(이름·전화번호 등)를 제3자에게 제공하지 않습니다. 다만 웹사이트 이용 과정에서 자동 수집되는 행태정보는 아래 4-2에 따라 광고·분석 사업자에게 제공됩니다.</p>
          </section>

          {/* 2026-09-22 추가(대표 지시) — 광고·분석 도구 행태정보 고지. 외부 링크 금지 정책상 URL 은 텍스트로만 표기(a 태그 금지). */}
          <section>
            <h2>4-2. 행태정보의 수집·이용 및 제3자 제공</h2>
            <p className="effective-date">본 조항 시행일자: 2026년 9월 22일</p>
            <p>
              회사는 광고 성과 측정, 이용자 관심에 맞춘 광고 제공, 부정클릭 방지 및 서비스 개선을 위하여
              아래 광고·분석 사업자의 도구(픽셀·스크립트·쿠키)를 이용해 이용자의 웹사이트 이용 행태정보를
              자동으로 수집하며, 수집된 행태정보는 아래 사업자에게 제공(전송)됩니다.
            </p>
            <table className="info-table">
              <thead>
                <tr>
                  <th>사업자</th>
                  <th>이용 도구</th>
                  <th>이용 목적</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Meta Platforms, Inc.</td>
                  <td>페이스북·인스타그램 픽셀, 전환 API(Conversions API)</td>
                  <td>광고 성과 측정, 맞춤형 광고</td>
                </tr>
                <tr>
                  <td>Google LLC</td>
                  <td>Google Analytics 4, Google Ads</td>
                  <td>방문 통계 분석, 광고 성과 측정, 맞춤형 광고</td>
                </tr>
                <tr>
                  <td>㈜카카오</td>
                  <td>카카오 픽셀</td>
                  <td>광고 성과 측정, 맞춤형 광고</td>
                </tr>
                <tr>
                  <td>네이버㈜</td>
                  <td>네이버 애널리틱스</td>
                  <td>방문 통계 분석, 광고 성과 측정</td>
                </tr>
                <tr>
                  <td>Microsoft Corporation</td>
                  <td>Microsoft Clarity</td>
                  <td>이용 행태 분석, 서비스 개선</td>
                </tr>
                <tr>
                  <td>Smartlog(스마트로그)</td>
                  <td>Smartlog 방문 분석</td>
                  <td>방문 통계 분석, 광고 성과 측정</td>
                </tr>
                <tr>
                  <td>클릭바로(clickbaro)</td>
                  <td>클릭바로 방문 분석 스크립트</td>
                  <td>부정클릭 방지, 방문 분석</td>
                </tr>
              </tbody>
            </table>
            <ul>
              <li>
                <strong>수집 항목:</strong> 쿠키 및 광고 식별자, 방문 페이지·유입 경로(UTM 등 광고 매개변수 포함),
                접속 IP 주소·브라우저 및 기기 정보, 상담 문의·전화 걸기·카카오톡 상담 버튼 클릭 등 서비스 이용 행태
              </li>
              <li>
                <strong>제공하지 않는 정보:</strong> 상담 문의 시 입력한 이름·전화번호 원문은 광고·분석 사업자에게 제공하지 않습니다.
                다만 Meta 전환 API를 설정한 경우에는 이름·전화번호를 복원할 수 없도록 암호화(해시)한 값만 전송합니다.
              </li>
              <li>
                <strong>수집 방법:</strong> 이용자가 웹사이트를 방문하거나 버튼을 누를 때 각 사업자의 도구를 통해 자동 수집
              </li>
              <li>
                <strong>이용 목적:</strong> 광고 성과 측정, 맞춤형 광고 제공, 부정클릭 방지, 서비스 개선
              </li>
              <li>
                <strong>보유·이용 기간:</strong> 각 사업자의 개인정보 처리방침에서 정한 기간에 따릅니다.
                회사가 직접 보관하는 상담 문의 기록은 제3조(개인정보의 처리 및 보유 기간)에 따릅니다.
              </li>
            </ul>
            <p><strong>행태정보 수집 거부 방법</strong></p>
            <p>
              이용자는 아래 방법으로 행태정보 수집 및 맞춤형 광고를 거부할 수 있습니다.
              거부하더라도 분양 정보 조회 등 서비스 이용에는 제한이 없으나, 맞춤형 광고가 표시되지 않을 수 있습니다.
            </p>
            <ul>
              <li>
                <strong>웹 브라우저 쿠키 차단:</strong> Chrome — 설정 &gt; 개인정보 보호 및 보안 &gt; 서드 파티 쿠키 /
                Safari — 설정 &gt; 개인정보 보호 &gt; 크로스 사이트 추적 방지 / Edge — 설정 &gt; 쿠키 및 사이트 권한
              </li>
              <li>
                <strong>모바일 광고 식별자 차단:</strong> Android — 설정 &gt; Google &gt; 광고 &gt; 광고 ID 삭제 /
                iOS — 설정 &gt; 개인정보 보호 및 보안 &gt; 추적 &gt; '앱이 추적을 요청하도록 허용' 끄기
              </li>
              <li>
                <strong>사업자별 광고 설정:</strong> Meta 광고 기본 설정(www.facebook.com/adpreferences),
                Google 내 광고 센터(myadcenter.google.com), Google 애널리틱스 차단 브라우저 부가기능(tools.google.com/dlpage/gaoptout),
                카카오·네이버·Microsoft·Smartlog·클릭바로는 각 사업자의 개인정보 처리방침 및 광고 설정 메뉴
              </li>
            </ul>
            <p style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280' }}>
              ※ 위 주소는 안내용 표기이며, 주소창에 직접 입력하여 이용하실 수 있습니다.
              행태정보 관련 문의는 제8조의 개인정보 보호책임자(1533-8124)에게 하실 수 있습니다.
            </p>
          </section>

          <section>
            <h2>5. 정보주체의 권리·의무 및 행사방법</h2>
            <p>정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다.</p>
            <ul>
              <li>개인정보 열람 요구</li>
              <li>오류 등이 있을 경우 정정 요구</li>
              <li>삭제 요구</li>
              <li>처리정지 요구</li>
            </ul>
            <p>
              권리 행사는 회사에 대해 서면, 전화(1533-8124), 전자우편(lwkmlb@naver.com) 등을 통하여 하실 수 있으며
              회사는 이에 대해 지체 없이 조치하겠습니다.
            </p>
            <ul>
              <li><strong>회원 탈퇴:</strong> 마이페이지 &gt; 회원 탈퇴에서 직접 하실 수 있으며, 탈퇴 시 회원 정보는 지체 없이 삭제됩니다.</li>
              <li><strong>마케팅 수신 철회:</strong> 마이페이지의 &lsquo;마케팅 정보 수신&rsquo; 스위치를 끄거나 전화로 요청하시면 즉시 반영됩니다.</li>
            </ul>
          </section>

          <section>
            <h2>6. 개인정보의 안전성 확보 조치</h2>
            <p>회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.</p>
            <ul>
              <li><strong>관리적 조치:</strong> 개인정보 취급자를 최소한으로 지정하고 관리자 화면 접근을 제한</li>
              <li><strong>기술적 조치:</strong> 상담·문의 전화번호 암호화 저장(AES-256), 전송 구간 암호화(HTTPS), 관리자 로그인·권한 관리, 관리자 작업 기록 보관</li>
              <li><strong>물리적 조치:</strong> 서버 보관 장소의 출입 통제</li>
            </ul>
          </section>

          <section id="marketing">
            <h2>7. 마케팅(광고성 정보) 수신 동의 — 할인·분양 소식 받기</h2>
            <p>
              회사는 수신에 동의한 회원에게 <strong>카카오톡, 문자(SMS/LMS), 전화</strong>로 다음과 같은 광고성 정보를 안내할 수 있습니다.
            </p>
            <ul>
              <li>분양 현장의 할인·혜택 변경, 잔여세대·재오픈 소식</li>
              <li>신규 분양 현장 및 관심 지역 분양 일정</li>
              <li>분양 상담 안내, 이벤트 및 프로모션</li>
            </ul>
            <p>
              수신에 동의한 회원에게는 공개 전 잔여세대·할인율 변경 소식을 먼저 안내합니다.
            </p>
            <ul>
              <li><strong>이용 항목:</strong> 이름, 휴대전화번호, 열람·관심 현장 정보</li>
              <li><strong>보유 기간:</strong> 동의 철회 또는 회원 탈퇴 시까지</li>
              <li><strong>동의 방법:</strong> 로그인 화면의 [선택] &lsquo;할인·분양 소식 받기&rsquo; 체크, 현장 혜택 화면의 &lsquo;알림 받기&rsquo;, 마이페이지 스위치</li>
            </ul>
            <p>
              마케팅 수신 동의는 선택사항이며, 동의하지 않아도 회원 가입과 서비스 이용(회원 전용 혜택 열람 포함)에 제한이 없습니다.
              동의하지 않은 회원에게는 회원이 직접 문의하지 않는 한 광고성 연락을 하지 않습니다.
              동의한 경우에도 마이페이지 또는 전화로 언제든지 철회할 수 있으며, 철회 즉시 발송·연락이 중단됩니다.
              수신 동의·철회를 처리하면 그 결과(전송자, 처리 일시, 처리 내용)를 카카오 알림톡으로 알려 드리며,
              관련 법령에 따라 동의일로부터 2년마다 수신 동의 여부를 확인합니다.
            </p>
          </section>

          <section>
            <h2>8. 개인정보 보호책임자</h2>
            <p>
              회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의
              불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.
            </p>
            <div className="contact-info">
              <p><strong>개인정보 보호책임자</strong></p>
              <p>직책: 큐비컴 대표</p>
              <p>연락처: 1533-8124</p>
              <p>전자우편: lwkmlb@naver.com</p>
            </div>
            <p style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280' }}>
              개인정보 침해에 대한 신고·상담은 개인정보침해신고센터(국번 없이 118), 개인정보분쟁조정위원회(1833-6972)에도 하실 수 있습니다.
            </p>
          </section>

          <section>
            <h2>9. 개인정보 처리방침 변경</h2>
            <p>
              이 개인정보 처리방침은 2026년 10월 5일부터 적용됩니다(이전 방침은 2024년 1월 1일 시행).
              법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터
              이 페이지를 통하여 고지할 것입니다.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
